import ax, { AxiosInstance } from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import {
  clearAuthSession,
  readAuthTokensFromResponse,
  refreshAuthSession,
} from "services/auth-session.service";
import { accessTokenOperations } from "services/sub-services/token-service";

const authNetworkTimeoutMs = 60_000;

const authNetworkService = ax.create({
  timeout: authNetworkTimeoutMs,
  headers: {
    Accept: "application/json",
  },
});

export const syncAuthNetworkAccessToken = (accessToken?: string | null) => {
  if (accessToken) {
    authNetworkService.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    return;
  }

  delete authNetworkService.defaults.headers.common.Authorization;
};

const refreshTokenLogic = async (failedRequest: { response?: { config?: { headers?: any } } }) => {
  try {
    const apiResponse = await refreshAuthSession();
    const { accessToken: newAccessToken } = readAuthTokensFromResponse(apiResponse);

    syncAuthNetworkAccessToken(newAccessToken || null);

    if (newAccessToken && failedRequest.response?.config) {
      failedRequest.response.config.headers = failedRequest.response.config.headers || {};
      failedRequest.response.config.headers.Authorization = `Bearer ${newAccessToken}`;
    }
  } catch (error) {
    syncAuthNetworkAccessToken(null);
    clearAuthSession();
    throw error;
  }
};

createAuthRefreshInterceptor(authNetworkService, refreshTokenLogic, {
  pauseInstanceWhileRefreshing: true,
});

const createAttachTokenInterceptor = (axiosInstance: AxiosInstance, getAccessToken: Function) => {
  const attachAccessToken = (requestConfig: any) => {
    const accessToken = getAccessToken();
    requestConfig.headers = requestConfig.headers || {};
    const headers = requestConfig.headers;

    if (typeof FormData !== "undefined" && requestConfig.data instanceof FormData) {
      if (typeof headers?.delete === "function") {
        headers.delete("Content-Type");
        headers.delete("content-type");
      } else if (headers) {
        delete headers["Content-Type"];
        delete headers["content-type"];
      }
    }

    if (accessToken) {
      requestConfig.headers.Authorization = `Bearer ${accessToken}`;
    } else if (requestConfig.headers?.Authorization) {
      delete requestConfig.headers.Authorization;
    }

    return requestConfig;
  };

  return axiosInstance.interceptors.request.use(attachAccessToken);
};

createAttachTokenInterceptor(authNetworkService, accessTokenOperations.get);

export default authNetworkService;
